import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export const Eyebrow = ({ children, testId }) => (
  <div
    data-testid={testId}
    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-elv-green/90"
  >
    <span className="h-1 w-1 rounded-full bg-elv-green" />
    {children}
  </div>
);

export const SectionHeading = ({ eyebrow, title, sub, align = "left", testId }) => (
  <div
    data-testid={testId}
    className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""}`}
  >
    {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
    <h2 className="mt-5 text-3xl font-semibold leading-[1.08] text-elv-text sm:text-4xl lg:text-[2.9rem]">
      {title}
    </h2>
    {sub ? <p className="mt-5 text-sm text-elv-dim md:text-base">{sub}</p> : null}
  </div>
);

export const GlassCard = ({ className = "", children, testId, hover = true }) => (
  <div
    data-testid={testId}
    className={`glass ${hover ? "glass-hover" : ""} relative rounded-lg ${className}`}
  >
    {children}
  </div>
);

export const CTA = ({ to, children, variant = "primary", testId, onClick, type }) => {
  const styles = {
    primary:
      "bg-elv-green text-[#04120A] hover:bg-[#5cffa0] shadow-[0_10px_40px_-16px_rgba(0,255,102,0.8)]",
    ghost: "glass text-elv-text hover:border-elv-cyan/40",
    quiet: "text-elv-dim hover:text-elv-text",
  }[variant];
  const cls = `group inline-flex items-center justify-center gap-2 rounded-md px-6 py-3 text-sm font-semibold tracking-tight transition-colors duration-300 ${styles}`;
  if (to)
    return (
      <Link to={to} data-testid={testId} className={cls}>
        {children}
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
      </Link>
    );
  return (
    <button type={type || "button"} onClick={onClick} data-testid={testId} className={cls}>
      {children}
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
    </button>
  );
};

export const DemoTag = ({ children = "Demonstration data", testId }) => (
  <span
    data-testid={testId}
    className="inline-flex items-center gap-1.5 rounded-sm border border-elv-cyan/25 bg-elv-cyan/5 px-2 py-1 text-[10px] uppercase tracking-[0.18em] text-elv-cyan"
  >
    {children}
  </span>
);

export const Atmosphere = ({ variant = "hero" }) => (
  <>
    <div className="pointer-events-none absolute inset-0 grid-lines opacity-60" />
    <div className="pointer-events-none absolute inset-0 grain" />
    {variant === "hero" ? (
      <>
        <div className="pointer-events-none absolute -top-40 left-1/2 h-[46rem] w-[46rem] -translate-x-1/2 animate-drift rounded-full bg-[radial-gradient(circle,rgba(0,255,102,0.14),transparent_62%)] blur-3xl" />
        <div className="pointer-events-none absolute -right-40 top-40 h-[34rem] w-[34rem] animate-drift rounded-full bg-[radial-gradient(circle,rgba(0,255,255,0.10),transparent_65%)] blur-3xl" />
        <div className="pointer-events-none absolute -left-52 bottom-0 h-[30rem] w-[30rem] rounded-full bg-[radial-gradient(circle,rgba(107,76,154,0.14),transparent_68%)] blur-3xl" />
      </>
    ) : (
      <div className="pointer-events-none absolute inset-x-0 top-0 h-64 bg-[radial-gradient(60%_100%_at_50%_0%,rgba(0,255,102,0.07),transparent_70%)]" />
    )}
    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-elv-base" />
  </>
);

export const Shell = ({ children, className = "" }) => (
  <div className={`mx-auto w-full max-w-[1240px] px-5 sm:px-8 ${className}`}>{children}</div>
);

export const PageHeader = ({ eyebrow, title, sub, testId }) => (
  <section className="relative overflow-hidden border-b border-elv-line pb-16 pt-28 sm:pt-36">
    <Atmosphere variant="page" />
    <Shell>
      <div className="relative max-w-4xl reveal">
        {eyebrow ? <Eyebrow testId={testId ? `${testId}-eyebrow` : undefined}>{eyebrow}</Eyebrow> : null}
        <h1
          data-testid={testId}
          className="mt-6 text-4xl font-semibold leading-[1.04] text-elv-text sm:text-5xl lg:text-6xl"
        >
          {title}
        </h1>
        {sub ? <p className="mt-6 max-w-2xl text-base text-elv-dim md:text-lg">{sub}</p> : null}
      </div>
    </Shell>
  </section>
);

export const StatusChip = ({ status }) => {
  const map = {
    connected: ["Connected", "text-elv-green border-elv-green/30 bg-elv-green/10"],
    not_connected: ["Not Connected", "text-elv-dim border-white/10 bg-white/5"],
    needs_configuration: ["Needs Configuration", "text-amber-300 border-amber-300/30 bg-amber-300/10"],
    error: ["Error", "text-red-300 border-red-400/30 bg-red-400/10"],
    operational: ["Operational", "text-elv-green border-elv-green/30 bg-elv-green/10"],
    active: ["Active", "text-elv-green border-elv-green/30 bg-elv-green/10"],
    paused: ["Paused", "text-elv-dim border-white/10 bg-white/5"],
  };
  const [label, cls] = map[status] || [String(status || "—"), "text-elv-dim border-white/10 bg-white/5"];
  return (
    <span
      data-testid={`status-chip-${status}`}
      className={`inline-flex items-center gap-1.5 rounded-sm border px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] ${cls}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {label}
    </span>
  );
};
